/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: useFirewallRulesList.tsx
 ??? Created: 2025-01-XX
 ?? Description:
    Hook for managing Firewall Rules list with DataGrid
----------------------------------------------------------------*/

import { useMemo } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { fetchFirewallRules } from '../api/firewallRulesApi';
import type { FirewallRule } from '../types';

export function useFirewallRulesList() {
  const { t } = useI18n();

  const availableColumns = useMemo(
    () => [
      {
        key: 'name',
        label: t('firewallRules.columns.name'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: FirewallRule) => (
          <span className="font-medium text-foreground">{item.name}</span>
        ),
      },
      {
        key: 'action',
        label: t('firewallRules.columns.action'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: FirewallRule) => {
          const actionColors: Record<string, string> = {
            accept: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
            drop: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
            reject: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
          };
          return (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium uppercase ${
                actionColors[item.action] || 'bg-gray-100 text-gray-800'
              }`}
            >
              {item.action}
            </span>
          );
        },
      },
      {
        key: 'direction',
        label: t('firewallRules.columns.direction'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: FirewallRule) => (
          <span className="text-sm text-muted-foreground uppercase">{item.direction}</span>
        ),
      },
      {
        key: 'service',
        label: t('firewallRules.columns.service'),
        sortable: false,
        visible: true,
        render: (_value: unknown, item: FirewallRule) => (
          <span className="text-sm text-foreground">
            {item.service ? `${item.service.name} (${item.service.protocol}/${item.service.port})` : '-'}
          </span>
        ),
      },
      {
        key: 'priority',
        label: t('firewallRules.columns.priority'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: FirewallRule) => (
          <span className="text-sm text-foreground font-mono">{item.priority}</span>
        ),
      },
      {
        key: 'enabled',
        label: t('firewallRules.columns.status'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: FirewallRule) => (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
              item.enabled
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
            }`}
          >
            {item.enabled ? t('common.enabled') : t('common.disabled')}
          </span>
        ),
      },
      {
        key: 'actions',
        label: t('common.actions'),
        sortable: false,
        visible: true,
      },
    ],
    [t]
  );

  const fetchData = useMemo(
    () => async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      const result = await fetchFirewallRules({
        ...params,
      });

      return {
        data: result.data,
        pagination: result.pagination,
      };
    },
    []
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'priority',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'firewallRules',
  });

  const rules = useMemo(() => data as FirewallRule[], [data]);

  return {
    rules,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  };
}
